// LT-12 — "Puantajlarım": the employee's own weeks and their sign-off.
//
// Before this card the only caller of submit was a helper nobody rendered, so a
// sheet could sit `open` for ever unless someone reached it by API: the manager
// queue has nothing to approve until the employee submits. The card gives the
// person two things, and both read the server rather than compute anything:
//
//   * the week they are looking at, day by day (this week by default — a week that
//     has no sheet yet still shows its approved leave), and
//   * their own sheets with the totals lock would persist now, and a Submit button
//     for the ones still theirs to sign off (open or reopened).
//
// Submit owns its idempotency key for the life of one ambiguous outcome, like the
// manager transitions. It needs no navigation blocker: open → submitted is a
// single-shot transition, so a lost intent replays as a definitive 409, never as a
// second write.

import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";

import {
  Badge,
  Banner,
  Button,
  Card,
  DataTable,
  Empty,
  ErrorState,
  Loading,
  type DataTableColumn,
} from "@novemai/components";

import { EnvelopeError, errorMessage } from "@/api/errors";
import { useKeysetQuery } from "@/api/keyset";
import { useTimeZone } from "@/api/use-time-zone";
import { formatDateRange } from "@/lib/date-range";
import { formatHours } from "@/lib/hours";

import {
  fetchOwnTimesheetsPage,
  fetchWeekDays,
  submitTimesheet,
  type TimesheetRow,
} from "./time-api";
import { TIMESHEET_STATUS_TONE } from "./time-status";
import { TimesheetDaysDialog, WeekDaysPanel } from "./timesheet-days-view";
import { currentWeekStart, shiftWeek } from "./week";

interface PendingSubmitIntent {
  readonly timesheetId: string;
  readonly idempotencyKey: string;
}

/** A sheet is the employee's to sign off while open, and again once it is reopened. */
function canSubmit(row: TimesheetRow): boolean {
  return row.status === "open" || row.status === "reopened";
}

export function MyTimesheetsCard({
  employeeId,
}: {
  readonly employeeId: string;
}): React.ReactElement {
  const { t, i18n } = useTranslation();
  const timeZone = useTimeZone();
  const queryClient = useQueryClient();
  const thisWeek = useMemo(() => currentWeekStart(new Date(), timeZone), [timeZone]);
  // null follows the tenant's current week; a chosen week sticks until "this week".
  const [chosenWeek, setChosenWeek] = useState<string | null>(null);
  const weekStart = chosenWeek ?? thisWeek;
  const [daysFor, setDaysFor] = useState<string | null>(null);
  const pendingSubmit = useRef<PendingSubmitIntent | null>(null);

  const weekQuery = useQuery({
    queryKey: ["time", "week-days", employeeId, weekStart],
    queryFn: () => fetchWeekDays(weekStart, employeeId),
  });
  const sheets = useKeysetQuery<TimesheetRow>({
    queryKey: ["time", "timesheets", "own", employeeId],
    fetchPage: (cursor) => fetchOwnTimesheetsPage(employeeId, cursor),
  });
  // Newest week first, decided over the loaded window like the shifts card: the
  // server pages by creation time and one person's sheets fit a page.
  const rows = useMemo(
    () => [...sheets.items].sort((a, b) => b.period_start.localeCompare(a.period_start)),
    [sheets.items],
  );

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ["time", "timesheets"] });
    void queryClient.invalidateQueries({ queryKey: ["time", "week-days"] });
    void queryClient.invalidateQueries({ queryKey: ["time", "timesheet-days"] });
  };

  const submit = useMutation({
    mutationFn: (intent: PendingSubmitIntent) =>
      submitTimesheet(intent.timesheetId, intent.idempotencyKey),
    onSuccess: () => {
      pendingSubmit.current = null;
      refresh();
    },
    onError: (error) => {
      // A known 4xx is the server's final word (e.g. the manager already acted);
      // anything else may have committed, so the same key replays.
      if (error instanceof EnvelopeError && error.httpStatus >= 400 && error.httpStatus < 500) {
        pendingSubmit.current = null;
        refresh();
      }
    },
  });

  const startSubmit = (timesheetId: string): void => {
    if (submit.isPending) return;
    const current = pendingSubmit.current;
    // While an outcome is ambiguous only that same sheet may replay.
    if (current !== null && current.timesheetId !== timesheetId) return;
    const intent = current ?? { timesheetId, idempotencyKey: crypto.randomUUID() };
    pendingSubmit.current = intent;
    submit.mutate(intent);
  };

  const hours = (value: string): string => formatHours(value, i18n.language) ?? value;

  const columns: readonly DataTableColumn<TimesheetRow>[] = [
    {
      id: "period",
      header: t("time.column.period"),
      cell: (row): string => formatDateRange(i18n.language, row.period_start, row.period_end),
    },
    {
      id: "regular",
      header: t("time.column.regular_hours"),
      align: "end",
      cell: (row): React.ReactNode => (
        <span className="font-mono tabular-nums text-[var(--fg-primary)]">
          {hours(row.total_regular_hours)}
        </span>
      ),
    },
    {
      id: "ot",
      header: t("time.column.ot_hours"),
      align: "end",
      cell: (row): React.ReactNode => (
        <span className="font-mono tabular-nums text-[var(--fg-secondary)]">
          {hours(row.total_ot_hours)}
        </span>
      ),
    },
    {
      id: "leave",
      header: t("time.column.leave_hours"),
      align: "end",
      cell: (row): React.ReactNode => (
        <span className="font-mono tabular-nums text-[var(--fg-secondary)]">
          {hours(row.total_pto_hours)}
        </span>
      ),
    },
    {
      id: "status",
      header: t("time.column.status"),
      cell: (row): React.ReactNode => (
        <div className="flex flex-wrap items-center gap-1.5">
          <Badge tone={TIMESHEET_STATUS_TONE[row.status] ?? "neutral"}>
            {t(`time.timesheet_status.${row.status}`)}
          </Badge>
          {row.provisional ? <Badge tone="neutral">{t("time.days.provisional")}</Badge> : null}
          {row.leave_day_worked_days > 0 ? (
            <Badge tone="danger">{t("time.days.leave_day_worked")}</Badge>
          ) : null}
        </div>
      ),
    },
    {
      id: "actions",
      header: t("time.column.actions"),
      align: "end",
      cell: (row): React.ReactNode => (
        <div className="flex justify-end gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={(): void => {
              setDaysFor(row.timesheet_id);
            }}
          >
            {t("time.days.view")}
          </Button>
          {canSubmit(row) ? (
            <Button
              variant="primary"
              size="sm"
              disabled={
                submit.isPending ||
                (pendingSubmit.current !== null &&
                  pendingSubmit.current.timesheetId !== row.timesheet_id)
              }
              onClick={(): void => {
                startSubmit(row.timesheet_id);
              }}
            >
              {t("time.action.submit")}
            </Button>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <Card>
      <div className="space-y-5 p-5">
        <div className="flex items-start gap-3">
          <div
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[10px]"
            style={{ background: "var(--accent-subtle-bg)" }}
            aria-hidden="true"
          >
            <CalendarDays size={20} color="var(--accent)" />
          </div>
          <div>
            <h2 className="font-serif text-[16px] text-[var(--fg-primary)]">
              {t("time.my_timesheets.title")}
            </h2>
            <p className="font-sans text-[13px] text-[var(--fg-muted)]">
              {t("time.my_timesheets.description")}
            </p>
          </div>
        </div>

        <section aria-labelledby="my-week-heading" className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 id="my-week-heading" className="font-serif text-[15px] text-[var(--fg-primary)]">
              {weekStart === thisWeek
                ? t("time.my_timesheets.this_week")
                : t("time.my_timesheets.chosen_week")}
            </h3>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                aria-label={t("time.my_timesheets.previous_week")}
                onClick={(): void => setChosenWeek(shiftWeek(weekStart, -1))}
              >
                <ChevronLeft size={16} aria-hidden="true" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                disabled={weekStart === thisWeek}
                onClick={(): void => setChosenWeek(null)}
              >
                {t("time.my_timesheets.back_to_this_week")}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                aria-label={t("time.my_timesheets.next_week")}
                onClick={(): void => setChosenWeek(shiftWeek(weekStart, 1))}
              >
                <ChevronRight size={16} aria-hidden="true" />
              </Button>
            </div>
          </div>
          {weekQuery.isLoading ? (
            <Loading label={t("time.days.loading")} rows={4} />
          ) : weekQuery.isError ? (
            <ErrorState
              title={t("time.days.error_title")}
              description={errorMessage(weekQuery.error, t)}
            />
          ) : weekQuery.data !== undefined ? (
            <WeekDaysPanel view={weekQuery.data} />
          ) : null}
        </section>

        {submit.isError ? (
          <Banner severity="danger" title={t("time.my_timesheets.submit_error_title")}>
            {errorMessage(submit.error, t)}
          </Banner>
        ) : null}

        <section aria-labelledby="my-sheets-heading" className="space-y-3">
          <h3 id="my-sheets-heading" className="font-serif text-[15px] text-[var(--fg-primary)]">
            {t("time.my_timesheets.sheets_title")}
          </h3>
          {sheets.isLoading ? (
            <Loading label={t("time.my_timesheets.loading")} rows={3} />
          ) : sheets.isError ? (
            <ErrorState
              title={t("time.my_timesheets.error_title")}
              description={errorMessage(sheets.error, t)}
            />
          ) : rows.length > 0 ? (
            <DataTable<TimesheetRow>
              caption={t("time.my_timesheets.table_caption")}
              columns={columns}
              rows={rows}
              rowKey={(row): string => row.timesheet_id}
              hasNextPage={sheets.hasNextPage}
              onLoadMore={sheets.fetchNextPage}
              isFetchingNextPage={sheets.isFetchingNextPage}
              loadMoreLabel={t("time.load_more")}
            />
          ) : (
            <Empty
              title={t("time.my_timesheets.empty_title")}
              description={t("time.my_timesheets.empty_description")}
            />
          )}
        </section>
      </div>

      {daysFor !== null ? (
        <TimesheetDaysDialog
          timesheetId={daysFor}
          employeeName={null}
          onClose={(): void => setDaysFor(null)}
        />
      ) : null}
    </Card>
  );
}
